/**
 * AppointmentsPage — Módulo Agenda
 * FASE 1: Tabs Dashboard, Calendario, Pendientes, Configuración (0257)
 * Acceso: solo ADMIN o usuarios vinculados como Professional en Agenda > Equipo.
 */
import React, { useState, useMemo, useEffect, useRef } from "react";
import { ThemeProvider } from "@material-ui/core/styles";
import { useHistory, useLocation } from "react-router-dom";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import { useAppointmentFilters } from "./hooks/useAppointmentFilters";
import { useAppointments } from "./hooks/useAppointments";
import AppointmentsCalendar from "./AppointmentsCalendar";
import AppointmentsDashboard from "./AppointmentsDashboard";
import AppointmentsHistorial from "./AppointmentsHistorial";
import AppointmentsWebLeads from "./AppointmentsWebLeads";
import AppointmentsConfig from "./AppointmentsConfig";
import AgendaSetupWizard from "./AgendaSetupWizard";
import AppointmentModal from "../../components/AppointmentModal";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import { useStyles } from "./styles";
import { getOnboardingConfig } from "../../services/onboarding.api";
import { canAccessAgenda as fetchCanAccessAgenda } from "../../services/users.api";
import { updateAppointmentStatus } from "../../services/appointments.api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const FILTER_STATUS_DEF = [
  { value: "", tKey: "all" },
  { value: "pending", tKey: "pending" },
  { value: "confirmed", tKey: "confirmed" },
  { value: "rescheduled", tKey: "rescheduled" },
  { value: "in_progress", tKey: "in_progress" },
  { value: "completed", tKey: "completed" },
  { value: "no_show", tKey: "no_show" },
  { value: "cancelled", tKey: "cancelled" },
];

/**
 * El tema global no declara `palette.background`, así que en dark MUI cae a su gris
 * por defecto (#424242 paper / #303030 default) — otra familia cromática que la de la
 * Agenda (#1f2937 / #111827). Eso se notaba en cada Dialog, Menu y Popover del módulo.
 * Un ThemeProvider anidado alcanza a todos los descendientes (los portales heredan el
 * tema del árbol React, no del DOM) sin alterar el resto de la aplicación.
 */
const withAgendaSurfaces = (outerTheme) => {
  const agenda = outerTheme.agenda;
  if (!agenda) return outerTheme;
  return {
    ...outerTheme,
    palette: {
      ...outerTheme.palette,
      background: {
        ...outerTheme.palette.background,
        paper: agenda.palette.card,
        default: agenda.palette.background,
      },
    },
  };
};

// Se monta dentro del ThemeProvider para que useStyles resuelva con el tema del módulo.
function AgendaRoot({ children }) {
  const classes = useStyles();
  return <div className={classes.agendaRoot}>{children}</div>;
}

export default function AppointmentsPage(props) {
  return (
    <ThemeProvider theme={withAgendaSurfaces}>
      <AgendaRoot>
        <AppointmentsPageContent {...props} />
      </AgendaRoot>
    </ThemeProvider>
  );
}

function AppointmentsPageContent() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const [tabIndex, setTabIndex] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [canAccessAgenda, setCanAccessAgenda] = useState(null);
  const [canAccessConfig, setCanAccessConfig] = useState(false);
  const [hasProfile, setHasProfile] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const filters = useAppointmentFilters();
  const params = useMemo(() => filters.params(), [filters.dateFrom, filters.dateTo, filters.professionalId, filters.status]);
  const { appointments, loading, fetchAppointments } = useAppointments(params);

  const statusOptions = useMemo(
    () =>
      FILTER_STATUS_DEF.map((o) => ({
        value: o.value,
        label: i18n.t(`appointmentsModule.status.${o.tKey}`),
      })),
    [i18n.language],
  );

  // «Solicitudes web» se inserta ANTES de Configuración, no al final: la última
  // posición es la única condicional (`canAccessConfig`), y meter ahí una
  // pestaña que todo el mundo puede ver obligaría a componer el array por
  // trozos. Índice 3 = solicitudes web · índice 4 = configuración.
  const tabLabels = useMemo(
    () => [
      i18n.t("appointmentsModule.page.tabs.dashboard"),
      i18n.t("appointmentsModule.page.tabs.calendar"),
      i18n.t("appointmentsModule.page.tabs.history"),
      i18n.t("appointmentsModule.page.tabs.webLeads"),
      i18n.t("appointmentsModule.page.tabs.settings"),
    ],
    [i18n.language],
  );

  // Índices desplazados al retirar «Pendientes» (migración 358): eran 4 y 5.
  const WEB_LEADS_TAB = 3;
  const CONFIG_TAB = 4;
  const HISTORY_TAB = 2;

  const handleEdit = (a) => { setSelectedAppointment(a); setModalOpen(true); };
  const handleCloseModal = () => { setSelectedAppointment(null); setModalOpen(false); fetchAppointments(); };
  const handleUpdateStatus = async (id, status) => {
    try {
      await updateAppointmentStatus(id, { status });
      fetchAppointments();
    } catch (e) { toastError(e); }
  };

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await fetchCanAccessAgenda();
        if (!cancelled && isMountedRef.current) {
          setCanAccessAgenda(data?.canAccessAgenda === true);
          setCanAccessConfig(data?.canAccessConfig === true);
        }
        if (data?.canAccessAgenda) {
          try {
            const cfg = await getOnboardingConfig();
            if (!cancelled && isMountedRef.current) {
              setHasProfile(!!cfg.data?.business?.profileId);
            }
          } catch {
            if (!cancelled && isMountedRef.current) setHasProfile(false);
          }
        }
      } catch (e) {
        if (!cancelled && isMountedRef.current) {
          setCanAccessAgenda(false);
          setCanAccessConfig(false);
          setHasProfile(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!canAccessConfig && tabIndex >= CONFIG_TAB) setTabIndex(HISTORY_TAB);
  }, [canAccessConfig, tabIndex]);

  // Tab desde navegación (ej. Kanban → icono reloj → Agenda Calendario)
  useEffect(() => {
    const fromState = location.state?.tabIndex;
    if (typeof fromState === "number" && fromState >= 0 && fromState <= CONFIG_TAB) {
      setTabIndex(fromState);
    }
  }, [location.state?.tabIndex]);

  const handleWizardCompleted = () => {
    setWizardOpen(false);
    setHasProfile(true);
    fetchAppointments();
  };

  if (canAccessAgenda === false) {
    return (
      <MainContainer>
        <MainHeader>
          <Title>{i18n.t("appointmentsModule.page.moduleTitle")}</Title>
        </MainHeader>
        <div style={{ padding: 24, textAlign: "center" }}>
          <Typography variant="h6" gutterBottom>
            {i18n.t("appointmentsModule.page.accessDeniedTitle")}
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
            {i18n.t("appointmentsModule.page.accessDeniedP1")}
            <br />
            {i18n.t("appointmentsModule.page.accessDeniedP2")}
          </Typography>
          <Button variant="contained" color="primary" onClick={() => history.push("/")}>
            {i18n.t("appointmentsModule.page.goHome")}
          </Button>
        </div>
      </MainContainer>
    );
  }

  if (canAccessAgenda === null || (canAccessAgenda === true && hasProfile === null)) {
    return (
      <MainContainer>
        <MainHeader><Title>{i18n.t("appointmentsModule.page.moduleTitle")}</Title></MainHeader>
        <div style={{ padding: 24, textAlign: "center" }}><Typography>{i18n.t("appointmentsModule.page.checkingAccess")}</Typography></div>
      </MainContainer>
    );
  }

  if (hasProfile === false) {
    const SECTORS = [
      { label: "Belleza",    bg: "#EC4899" },
      { label: "Comercial",  bg: "#F59E0B" },
      { label: "Salud",      bg: "#10B981" },
      { label: "Educación",  bg: "#3B82F6" },
      { label: "Técnico",    bg: "#8B5CF6" },
    ];
    return (
      <MainContainer>
        <MainHeader>
          <Title>{i18n.t("appointmentsModule.page.moduleTitle")}</Title>
        </MainHeader>
        <div className={classes.emptyStateWrap}>
          <div className={classes.emptyStateIcon}>
            <EventAvailableIcon />
          </div>
          <Typography variant="h4" style={{ fontWeight: 800, marginBottom: 12 }}>
            Agenda de Citas
          </Typography>
          <Typography variant="body1" color="textSecondary" style={{ marginBottom: 32, lineHeight: 1.6 }}>
            Gestiona citas, equipo, servicios y recordatorios automáticos en un solo lugar.
            Configúralo en 5 pasos en menos de 5 minutos.
          </Typography>
          <div style={{ display: "flex", justifyContent: "center", gap: 8, flexWrap: "wrap", marginBottom: 40 }}>
            {SECTORS.map(s => (
              <Chip key={s.label} label={s.label}
                style={{ backgroundColor: s.bg, color: "#fff", fontWeight: 600, fontSize: "0.8rem" }}
              />
            ))}
          </div>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={() => setWizardOpen(true)}
            style={{ minWidth: 240, fontWeight: 700, fontSize: "1rem", padding: "12px 32px" }}
          >
            Iniciar configuración →
          </Button>
        </div>
        <AgendaSetupWizard
          open={wizardOpen}
          onClose={() => setWizardOpen(false)}
          onCompleted={handleWizardCompleted}
        />
      </MainContainer>
    );
  }

  const TABS = canAccessConfig ? tabLabels : tabLabels.slice(0, CONFIG_TAB);

  const renderContent = () => {
    if (tabIndex === 0) return <AppointmentsDashboard appointments={appointments} />;
    if (tabIndex === 1) return <AppointmentsCalendar appointments={appointments} onSelectEvent={(e) => handleEdit(e?.resource)} />;
    if (tabIndex === 2) {
      return (
        <AppointmentsHistorial
          appointments={appointments}
          loading={loading}
          onEdit={handleEdit}
        />
      );
    }
    // Se monta sin props: trae sus propios datos de /web-leads. La agenda y el
    // buzón web son dos orígenes distintos y no comparten filtros.
    if (tabIndex === WEB_LEADS_TAB) return <AppointmentsWebLeads />;
    return <AppointmentsConfig />;
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("appointmentsModule.page.moduleTitle")}</Title>
        <MainHeaderButtonsWrapper>
          {/* Los filtros son de la agenda: no aplican al buzón web (que tiene
              el suyo) ni a configuración. */}
          {tabIndex < WEB_LEADS_TAB && (
            <>
              <TextField type="date" size="small" className={classes.nativeDateInput} value={filters.dateFrom} onChange={(e) => filters.setDateFrom(e.target.value)} InputLabelProps={{ shrink: true }} style={{ marginRight: 8 }} />
              <TextField type="date" size="small" className={classes.nativeDateInput} value={filters.dateTo} onChange={(e) => filters.setDateTo(e.target.value)} InputLabelProps={{ shrink: true }} style={{ marginRight: 8 }} />
              <TextField select size="small" value={filters.status} onChange={(e) => filters.setStatus(e.target.value)} style={{ minWidth: 120, marginRight: 8 }}>
                {statusOptions.map((o) => <MenuItem key={o.value || "all"} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
            </>
          )}
          <Button variant="contained" color="primary" onClick={() => { setSelectedAppointment(null); setModalOpen(true); }}>
            {i18n.t("appointmentsModule.page.newAppointment")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Tabs
        value={tabIndex}
        onChange={(_, v) => setTabIndex(v)}
        className={classes.tabs}
        textColor="primary"
        indicatorColor="primary"
      >
        {TABS.map((label) => (
          <Tab key={label} label={label} className={classes.tab} />
        ))}
      </Tabs>
      {renderContent()}
      {modalOpen && (
        <AppointmentModal
          open={modalOpen}
          onClose={handleCloseModal}
          appointment={selectedAppointment}
          onSaved={handleCloseModal}
          onStatusChange={handleUpdateStatus}
        />
      )}
    </MainContainer>
  );
}
